<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';
import AutomationPicker from './AutomationPicker.vue';
import AutomationPreviewAccordion from './AutomationPreviewAccordion.vue';
import SimulatorNodePreview from './SimulatorNodePreview.vue';
import { AUTOMATION_MODES, NODE_TYPES, createBranch } from '../helpers/sopFlow';

const props = defineProps({
  node: { type: Object, required: true },
  // Market of the SOP being edited; reference targets must be available there.
  market: { type: String, default: null },
  // The SOP being edited; it can't reference itself.
  sopId: { type: Number, default: null },
});

const emit = defineEmits(['removeBranch', 'deleteNode']);

const { t } = useI18n();

const pickerRef = ref(null);

// The panel intentionally edits the flow node in place — the node object is
// shared reactive state owned by the canvas, not panel-local data.
const nodeData = computed(() => props.node.data);

const isBranching = computed(() =>
  [NODE_TYPES.DECISION, NODE_TYPES.ROUTE].includes(props.node.type)
);
const isAction = computed(() => props.node.type === NODE_TYPES.ACTION);
const isAutomation = computed(() => props.node.type === NODE_TYPES.AUTOMATION);
const isReference = computed(() => props.node.type === NODE_TYPES.REFERENCE);

const sopStore = useOperatingProceduresStore();
const referenceOptions = computed(() =>
  sopStore.getRecords
    .filter(sop => sop.id !== props.sopId)
    .filter(sop => !sop.market || sop.market === props.market)
    .map(sop => ({ id: sop.id, name: sop.name }))
);
const selectedReference = computed({
  get: () =>
    referenceOptions.value.find(
      option => option.id === nodeData.value.operating_procedure_id
    ) || null,
  set: option => {
    nodeData.value.operating_procedure_id = option?.id || null;
  },
});

const addBranch = () => {
  nodeData.value.branches.push(createBranch());
};

const addItem = () => {
  nodeData.value.items.push('');
};

const updateItem = (index, value) => {
  nodeData.value.items[index] = value;
};

const removeItem = index => {
  nodeData.value.items.splice(index, 1);
};

const onAutomationSelected = automation => {
  nodeData.value.automation = automation;
};

const automationTypeLabel = computed(() => {
  const type = nodeData.value.automation?.type;
  return type ? t(`SOPS.AUTOMATION_TYPES.${type.toUpperCase()}`) : '';
});

const MODE_OPTIONS = [AUTOMATION_MODES.AUTO_PILOT, AUTOMATION_MODES.MANUAL];

// Nodes created before modes existed have no mode; treat them as manual.
const automationMode = computed(
  () => nodeData.value.mode || AUTOMATION_MODES.MANUAL
);

const setMode = mode => {
  nodeData.value.mode = mode;
};
</script>

<template>
  <div
    class="flex w-[42rem] shrink-0 flex-col gap-4 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <div class="flex items-center justify-between">
      <p class="mb-0 text-heading-3 text-n-slate-12">
        {{ t('SOPS.BUILDER.PANEL.TITLE') }}
      </p>
      <span class="text-xs uppercase tracking-wide text-n-slate-10">
        {{ t(`SOPS.NODES.${node.type.toUpperCase()}`) }}
      </span>
    </div>

    <div class="grid min-h-0 flex-1 grid-cols-2 gap-4">
      <div class="flex min-w-0 flex-col gap-4 overflow-y-auto">
        <TextArea
          v-model="nodeData.label"
          class="!h-auto shrink-0"
          :label="t('SOPS.BUILDER.PANEL.LABEL')"
          :placeholder="t('SOPS.BUILDER.PANEL.LABEL')"
          auto-height
          min-height="2.5rem"
          max-height="8rem"
        />

        <div v-if="isBranching" class="flex flex-col gap-2">
          <p class="mb-0 text-heading-3 text-n-slate-12">
            {{ t('SOPS.BUILDER.PANEL.BRANCHES') }}
          </p>
          <div
            v-for="branch in nodeData.branches"
            :key="branch.id"
            class="flex items-center gap-1"
          >
            <TextArea
              v-model="branch.label"
              class="min-w-0 flex-1 !h-auto"
              :placeholder="t('SOPS.BUILDER.PANEL.BRANCH_PLACEHOLDER')"
              auto-height
              min-height="2.5rem"
              max-height="8rem"
            />
            <Button
              icon="i-lucide-x"
              xs
              slate
              ghost
              @click="emit('removeBranch', branch.id)"
            />
          </div>
          <Button
            icon="i-lucide-plus"
            :label="t('SOPS.BUILDER.PANEL.ADD_BRANCH')"
            sm
            slate
            faded
            @click="addBranch"
          />
        </div>

        <div v-if="isAction" class="flex flex-col gap-2">
          <p class="mb-0 text-heading-3 text-n-slate-12">
            {{ t('SOPS.BUILDER.PANEL.ITEMS') }}
          </p>
          <div
            v-for="(item, index) in nodeData.items"
            :key="index"
            class="flex items-center gap-1"
          >
            <Input
              :model-value="item"
              class="min-w-0 flex-1"
              :placeholder="t('SOPS.BUILDER.PANEL.ITEM_PLACEHOLDER')"
              @update:model-value="updateItem(index, $event)"
            />
            <Button
              icon="i-lucide-x"
              xs
              slate
              ghost
              @click="removeItem(index)"
            />
          </div>
          <Button
            icon="i-lucide-plus"
            :label="t('SOPS.BUILDER.PANEL.ADD_ITEM')"
            sm
            slate
            faded
            @click="addItem"
          />
        </div>

        <div v-if="isAutomation" class="flex flex-col gap-2">
          <p class="mb-0 text-heading-3 text-n-slate-12">
            {{ t('SOPS.BUILDER.PANEL.AUTOMATION') }}
          </p>
          <div
            v-if="nodeData.automation"
            class="rounded-lg bg-n-alpha-black2 px-3 py-2"
          >
            <span class="text-xs uppercase tracking-wide text-n-slate-10">
              {{ automationTypeLabel }}
            </span>
            <AutomationPreviewAccordion
              :id="nodeData.automation.id"
              :type="nodeData.automation.type"
              :name="nodeData.automation.name"
            />
          </div>
          <Button
            :label="
              nodeData.automation
                ? t('SOPS.BUILDER.PANEL.CHANGE_AUTOMATION')
                : t('SOPS.BUILDER.PANEL.SELECT_AUTOMATION')
            "
            icon="i-lucide-zap"
            sm
            slate
            faded
            @click="pickerRef.open()"
          />

          <p class="mb-0 mt-2 text-heading-3 text-n-slate-12">
            {{ t('SOPS.BUILDER.PANEL.MODE') }}
          </p>
          <button
            v-for="mode in MODE_OPTIONS"
            :key="mode"
            type="button"
            class="rounded-lg border p-3 text-left transition-colors"
            :class="
              automationMode === mode
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="setMode(mode)"
          >
            <span class="flex items-center gap-2 text-sm text-n-slate-12">
              <span
                class="size-4"
                :class="
                  mode === 'auto_pilot' ? 'i-lucide-rocket' : 'i-lucide-hand'
                "
              />
              {{ t(`SOPS.BUILDER.PANEL.MODE_${mode.toUpperCase()}`) }}
            </span>
            <span class="mt-1 block text-xs text-n-slate-10">
              {{ t(`SOPS.BUILDER.PANEL.MODE_${mode.toUpperCase()}_HINT`) }}
            </span>
          </button>
        </div>

        <div v-if="isReference" class="flex flex-col gap-2">
          <p class="mb-0 text-heading-3 text-n-slate-12">
            {{ t('SOPS.BUILDER.PANEL.REFERENCE') }}
          </p>
          <SingleSelect
            v-model="selectedReference"
            :options="referenceOptions"
            :placeholder="t('SOPS.BUILDER.PANEL.SELECT_REFERENCE')"
            placeholder-icon="i-lucide-workflow"
          />
          <p class="mb-0 text-xs text-n-slate-10">
            {{ t('SOPS.BUILDER.PANEL.REFERENCE_MARKET_HINT') }}
          </p>
        </div>

        <div class="mt-auto border-t border-n-weak pt-3">
          <Button
            :label="t('SOPS.BUILDER.PANEL.DELETE_NODE')"
            icon="i-lucide-trash-2"
            sm
            ruby
            faded
            class="w-full"
            @click="emit('deleteNode')"
          />
        </div>
      </div>

      <div
        class="flex min-w-0 flex-col gap-2 overflow-y-auto rounded-lg bg-n-solid-3 p-3"
      >
        <p class="mb-2 text-heading-2 text-n-slate-12">
          {{ t('SOPS.BUILDER.PANEL.PREVIEW') }}
        </p>
        <SimulatorNodePreview :node="node" />
      </div>
    </div>

    <AutomationPicker ref="pickerRef" @select="onAutomationSelected" />
  </div>
</template>
